// Gemini AI Service for personalized place recommendations
// Uses the same API key as GoodWork Crafting

// 2026-04-24: AI läuft über Cloud Function `aiGateway`. GoogleGenerativeAI kommt nun aus dem Shim.
import { GoogleGenerativeAI } from './aiGatewayShim';
import type { Place } from '../types';
import type { ImportableData } from './craftingIntegrationService';

// Kein API-Key mehr im Frontend — Shim proxyt über Cloud Function.
const API_KEY = 'proxy';
const ai = new GoogleGenerativeAI(API_KEY);
const MODEL = 'gemini-2.5-flash';

// Valou area names for better context
const VALOU_AREA_NAMES: Record<string, string> = {
  'persoenlichkeitSkills': 'Persoenlichkeit & Skills',
  'taetigkeit': 'Arbeit & Beruf',
  'privatesLeben': 'Privates Leben',
  'stilWirkung': 'Stil & Wirkung',
  'ressourcenMittel': 'Ressourcen & Mittel',
  'gesundheit': 'Gesundheit'
};

/**
 * AI-generated recommendation with place-specific reasons
 */
export interface AIRecommendation {
  message: string;
  places: Array<{
    placeId: string;
    reason: string;
  }>;
  generatedAt: Date;
}

/**
 * Build context string from user's Crafting data
 */
function buildUserContext(craftingData: ImportableData): string {
  const parts: string[] = [];

  // Average Valou Mood (from all 6 life areas)
  if (craftingData.averageValouMood !== null) {
    const moodLevel = craftingData.averageValouMood < 5 ? 'niedrig' :
                      craftingData.averageValouMood < 7 ? 'mittel' : 'gut';
    parts.push(`Durchschnittlicher Valou-Wert: ${craftingData.averageValouMood}/10 (${moodLevel})`);
  }

  // Mental Load
  if (craftingData.averageMentalLoad !== null) {
    const loadLevel = craftingData.averageMentalLoad > 70 ? 'hoch' :
                      craftingData.averageMentalLoad > 50 ? 'mittel' : 'niedrig';
    parts.push(`Mental Load: ${craftingData.averageMentalLoad}% (${loadLevel})`);
  }

  // Wellbeing (Wetter-Stimmung)
  if (craftingData.currentWellbeing !== null) {
    const wellbeingLevel = craftingData.currentWellbeing < 5 ? 'niedrig' :
                           craftingData.currentWellbeing < 7 ? 'mittel' : 'gut';
    parts.push(`Aktuelles Wohlbefinden: ${craftingData.currentWellbeing}/10 (${wellbeingLevel})`);
  }

  // Valou Moods - all areas with their values
  if (craftingData.valouMoods) {
    const moodDetails: string[] = [];
    const lowMoodAreas: string[] = [];

    Object.entries(craftingData.valouMoods).forEach(([areaId, mood]) => {
      const areaName = VALOU_AREA_NAMES[areaId] || areaId;
      moodDetails.push(`${areaName}: ${mood}/10`);
      if (mood < 5) {
        lowMoodAreas.push(areaName);
      }
    });

    parts.push(`Valou-Bereiche: ${moodDetails.join(', ')}`);

    if (lowMoodAreas.length > 0) {
      parts.push(`Bereiche die Aufmerksamkeit brauchen: ${lowMoodAreas.join(', ')}`);
    }
  }

  // Personality
  if (craftingData.personality?.bigFiveScores) {
    const traits = craftingData.personality.bigFiveScores
      .sort((a, b) => b.score - a.score)
      .slice(0, 2)
      .map(t => t.label);
    parts.push(`Persoenlichkeit (Top Traits): ${traits.join(', ')}`);
  }

  // RIASEC
  if (craftingData.riasec?.hollandType) {
    parts.push(`RIASEC-Typ: ${craftingData.riasec.hollandType}`);
  }

  // Preferences - ALL data, no limits
  if (craftingData.valouPreferences?.vorlieben && craftingData.valouPreferences.vorlieben.length > 0) {
    parts.push(`Vorlieben: ${craftingData.valouPreferences.vorlieben.join(', ')}`);
  }

  if (craftingData.valouPreferences?.abneigungen && craftingData.valouPreferences.abneigungen.length > 0) {
    parts.push(`Abneigungen: ${craftingData.valouPreferences.abneigungen.join(', ')}`);
  }

  if (craftingData.valouPreferences?.mustHaves && craftingData.valouPreferences.mustHaves.length > 0) {
    parts.push(`Must-Haves: ${craftingData.valouPreferences.mustHaves.join(', ')}`);
  }

  if (craftingData.valouPreferences?.noGos && craftingData.valouPreferences.noGos.length > 0) {
    parts.push(`No-Gos: ${craftingData.valouPreferences.noGos.join(', ')}`);
  }

  // User name from Crafting profile
  if (craftingData.name) {
    parts.unshift(`Name: ${craftingData.name}`);
  }

  return parts.join('\n');
}

/**
 * Build places context for the prompt
 */
function buildPlacesContext(places: Place[]): string {
  // Only include approved places, limit to 20 for prompt size
  const approvedPlaces = places
    .filter(p => p.status === 'approved')
    .slice(0, 20);

  return approvedPlaces.map(p => {
    const parts = [
      `ID: ${p.id}`,
      `Name: ${p.name}`,
      p.category ? `Kategorie: ${p.category}` : null,
      p.goodFactor ? `GoodFactor: ${p.goodFactor}` : null,
      p.vibeTags && p.vibeTags.length > 0 ? `VibeTags: ${p.vibeTags.join(', ')}` : null,
      p.district ? `Stadtteil: ${p.district}` : null,
    ].filter(Boolean);
    return parts.join(' | ');
  }).join('\n');
}

/**
 * Generate personalized place recommendation using Gemini AI
 */
export async function generateAIRecommendation(
  craftingData: ImportableData,
  places: Place[],
  userName?: string
): Promise<AIRecommendation | null> {
  if (!ai) {
    console.warn('Gemini AI not initialized - missing API key');
    return null;
  }

  try {
    const userContext = buildUserContext(craftingData);
    const placesContext = buildPlacesContext(places);

    const prompt = `Du bist ein empathischer Wellbeing-Berater fuer die GoodPlace App.
Basierend auf dem aktuellen Zustand des Nutzers empfiehlst du passende Orte.

NUTZERPROFIL${craftingData.name || userName ? ` (${craftingData.name || userName})` : ''}:
${userContext}

VERFUEGBARE ORTE:
${placesContext}

AUFGABE:
1. Erstelle eine kurze, persoenliche Empfehlung (2-3 Saetze) basierend auf dem aktuellen Zustand.
   - Sprich den Nutzer direkt an (Du-Form)
   - Beziehe dich auf konkrete Werte (Mental Load, Wohlbefinden)
   - Sei empathisch und motivierend

2. Waehle 3 passende Orte aus der Liste und erklaere kurz (1 Satz), warum sie JETZT passen.

WICHTIG: Antworte NUR mit validem JSON im folgenden Format (keine Markdown-Formatierung):
{
  "message": "Deine persoenliche Empfehlung...",
  "places": [
    { "placeId": "exact-id-from-list", "reason": "Kurze Begruendung..." },
    { "placeId": "exact-id-from-list", "reason": "Kurze Begruendung..." },
    { "placeId": "exact-id-from-list", "reason": "Kurze Begruendung..." }
  ]
}`;

    const model = ai.getGenerativeModel({
      model: MODEL,
      generationConfig: {
        temperature: 0.7,
        topP: 0.8,
        maxOutputTokens: 8192,
        responseMimeType: 'application/json',
      },
    });

    // Retry logic for 503 errors
    let result;
    let retries = 3;
    while (retries > 0) {
      try {
        result = await model.generateContent(prompt);
        break;
      } catch (retryError: unknown) {
        const errorMessage = retryError instanceof Error ? retryError.message : String(retryError);
        if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
          retries--;
          if (retries > 0) {
            console.log(`Gemini overloaded, retrying in 2s... (${retries} left)`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw retryError;
          }
        } else {
          throw retryError;
        }
      }
    }

    if (!result) {
      return null;
    }

    const response = result.response;
    const text = response.text();

    // Log raw response for debugging
    console.log('Gemini raw response length:', text.length);
    console.log('Gemini raw response:', text);

    // Parse JSON from response (handle potential markdown wrapping)
    let jsonText = text.trim();
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.slice(7);
    }
    if (jsonText.startsWith('```')) {
      jsonText = jsonText.slice(3);
    }
    if (jsonText.endsWith('```')) {
      jsonText = jsonText.slice(0, -3);
    }
    jsonText = jsonText.trim();

    // Try to fix truncated JSON by closing it
    if (!jsonText.endsWith('}')) {
      console.warn('JSON appears truncated, attempting to fix...');
      // Try to close the JSON properly
      const lastBrace = jsonText.lastIndexOf('}');
      if (lastBrace > 0) {
        jsonText = jsonText.substring(0, lastBrace + 1);
        // Count brackets to see if we need more
        const openBrackets = (jsonText.match(/\[/g) || []).length;
        const closeBrackets = (jsonText.match(/\]/g) || []).length;
        const openBraces = (jsonText.match(/\{/g) || []).length;
        const closeBraces = (jsonText.match(/\}/g) || []).length;

        for (let i = 0; i < openBrackets - closeBrackets; i++) {
          jsonText += ']';
        }
        for (let i = 0; i < openBraces - closeBraces; i++) {
          jsonText += '}';
        }
      }
    }

    const parsed = JSON.parse(jsonText);

    // Validate response structure
    if (!parsed.message || !Array.isArray(parsed.places)) {
      console.error('Invalid AI response structure:', parsed);
      return null;
    }

    return {
      message: parsed.message,
      places: parsed.places.map((p: { placeId: string; reason: string }) => ({
        placeId: p.placeId,
        reason: p.reason,
      })),
      generatedAt: new Date(),
    };
  } catch (error) {
    console.error('Error generating AI recommendation:', error);
    return null;
  }
}

/**
 * Check if AI service is available
 */
export function isAIAvailable(): boolean {
  return ai !== null;
}

/**
 * Place data for AI content generation
 */
export interface PlaceDataForAI {
  name: string;
  category?: string;
  tagline?: string;
  description?: string;
  address?: string;
  city?: string;
  district?: string;
  moods?: string[];
  activities?: string[];
  community?: string[];
  vibeTags?: string[];
  // GoodWork-specific fields
  realm?: 'goodlife' | 'goodwork';
  goodworkCategory?: string;
  goodworkVibeTags?: string[];
  amenities?: {
    wifi?: string;
    powerOutlets?: string;
    meetingRooms?: boolean;
    phoneBooths?: boolean;
    kitchen?: boolean;
    monitors?: boolean;
  };
  workspaces?: {
    totalSeats?: number;
    quietZone?: boolean;
    teamArea?: boolean;
    outdoorSeats?: number;
  };
}

/**
 * AI-generated GoodFactor and Pitch suggestions
 */
export interface GoodFactorSuggestion {
  goodFactor: string;
  pitch: string;
  generatedAt: Date;
}

/**
 * Generate GoodFactor and Pitch suggestions for a place using AI
 * Uses Google Search Grounding for verified, real information
 * The text should reflect the GoodPlace philosophy and be SPECIFIC to this place
 */
export async function generateGoodFactorSuggestion(
  placeData: PlaceDataForAI
): Promise<GoodFactorSuggestion | null> {
  if (!ai) {
    console.warn('Gemini AI not initialized - missing API key');
    return null;
  }

  try {
    const isGoodWork = placeData.realm === 'goodwork';

    // Build search query for grounding - helps AI find relevant information
    const searchQuery = [
      placeData.name,
      placeData.city,
      placeData.address,
      isGoodWork ? 'Coworking Arbeitsplatz' : 'Cafe Restaurant Ort'
    ].filter(Boolean).join(' ');

    console.log('Using Google Search Grounding for:', searchQuery);

    // Build detailed context from place data
    let placeContext: string;

    if (isGoodWork) {
      // GoodWork-specific context
      const amenitiesInfo: string[] = [];
      if (placeData.amenities) {
        if (placeData.amenities.wifi) amenitiesInfo.push(`WLAN: ${placeData.amenities.wifi}`);
        if (placeData.amenities.powerOutlets) amenitiesInfo.push(`Steckdosen: ${placeData.amenities.powerOutlets}`);
        if (placeData.amenities.meetingRooms) amenitiesInfo.push('Meeting-Raeume vorhanden');
        if (placeData.amenities.phoneBooths) amenitiesInfo.push('Telefonkabinen vorhanden');
        if (placeData.amenities.kitchen) amenitiesInfo.push('Kueche/Kaffee vorhanden');
        if (placeData.amenities.monitors) amenitiesInfo.push('Externe Monitore verfuegbar');
      }

      const workspaceInfo: string[] = [];
      if (placeData.workspaces) {
        if (placeData.workspaces.totalSeats) workspaceInfo.push(`${placeData.workspaces.totalSeats} Arbeitsplaetze`);
        if (placeData.workspaces.quietZone) workspaceInfo.push('Ruhezone vorhanden');
        if (placeData.workspaces.teamArea) workspaceInfo.push('Teambereich vorhanden');
        if (placeData.workspaces.outdoorSeats) workspaceInfo.push(`${placeData.workspaces.outdoorSeats} Outdoor-Plaetze`);
      }

      placeContext = [
        `NAME: "${placeData.name}"`,
        placeData.goodworkCategory ? `GOODWORK-KATEGORIE: ${placeData.goodworkCategory}` : null,
        placeData.city ? `STADT: ${placeData.city}` : null,
        placeData.district ? `STADTTEIL: ${placeData.district}` : null,
        placeData.address ? `ADRESSE: ${placeData.address}` : null,
        placeData.tagline ? `TAGLINE: "${placeData.tagline}"` : null,
        placeData.description ? `BESCHREIBUNG: "${placeData.description}"` : null,
        placeData.goodworkVibeTags?.length ? `GOODWORK-VIBE-TAGS: ${placeData.goodworkVibeTags.join(', ')}` : null,
        amenitiesInfo.length ? `AUSSTATTUNG: ${amenitiesInfo.join(', ')}` : null,
        workspaceInfo.length ? `ARBEITSPLAETZE: ${workspaceInfo.join(', ')}` : null,
      ].filter(Boolean).join('\n');
    } else {
      // GoodLife context (original)
      placeContext = [
        `NAME: "${placeData.name}"`,
        placeData.category ? `KATEGORIE: ${placeData.category}` : null,
        placeData.city ? `STADT: ${placeData.city}` : null,
        placeData.district ? `STADTTEIL: ${placeData.district}` : null,
        placeData.address ? `ADRESSE: ${placeData.address}` : null,
        placeData.tagline ? `TAGLINE: "${placeData.tagline}"` : null,
        placeData.description ? `BESCHREIBUNG: "${placeData.description}"` : null,
        placeData.moods?.length ? `ATMOSPHAERE: ${placeData.moods.join(', ')}` : null,
        placeData.activities?.length ? `AKTIVITAETEN: ${placeData.activities.join(', ')}` : null,
        placeData.community?.length ? `COMMUNITY: ${placeData.community.join(', ')}` : null,
        placeData.vibeTags?.length ? `VIBE-TAGS: ${placeData.vibeTags.join(', ')}` : null,
      ].filter(Boolean).join('\n');
    }

    // Different prompts for GoodLife vs GoodWork
    let prompt: string;

    if (isGoodWork) {
      // GoodWork-specific prompt with Exzellenzfaktor and RESEARCH REQUIREMENT
      prompt = `Du schreibst fuer die GoodPlace-Plattform ueber "${placeData.name}" als ARBEITSORT.

WICHTIG - RECHERCHE-PFLICHT:
Du MUSST echte, verifizierte Informationen ueber diesen Ort verwenden!
- Recherchiere den Ort im Internet bevor du schreibst
- Verwende NUR Fakten die du verifizieren kannst (Oeffnungszeiten, Preise, Geschichte, Ausstattung, etc.)
- ERFINDE KEINE Details! Wenn du etwas nicht verifizieren kannst, lass es weg
- Nutze reale Besonderheiten des Ortes statt generischer Beschreibungen

WICHTIG - AUSGABE-REGELN:
- Schreibe KEINE Quellenangaben, URLs oder Referenzen im Text
- Vermeide Wiederholungen - jede Information nur EINMAL nennen
- Nutze NUR Fakten aus deiner Web-Recherche, NICHT aus den "ORT-INFOS" unten
- Die ORT-INFOS sind nur Kontext - recherchiere selbst aktuelle Informationen
- Wenn du nichts Verifiziertes findest, schreibe einen allgemeineren Text ohne erfundene Details

SUCHHINWEIS: "${placeData.name}"${placeData.city ? ` ${placeData.city}` : ''}${placeData.address ? ` ${placeData.address}` : ''}

GoodWork Places sind besondere Arbeitsorte mit Charakter:
- Konzentration und Deep Work (#fokussiert)
- Professionelle Infrastruktur (#professionell)
- Kreative Inspiration (#inspirierend)
- Vernetzung und Community (#community)

WICHTIG: Jeder GoodWork Ort braucht einen EXZELLENZFAKTOR - ein "Warum hier?", eine Geschichte, ein Alleinstellungsmerkmal, etwas das ihn unverwechselbar macht. Basiere dies auf ECHTEN Fakten!

ORT-INFOS (vom User angegeben - nur als Kontext, NICHT kopieren):
${placeContext}

SCHREIBE ZWEI TEXTE basierend auf recherchierten Fakten:

1. goodFactor (100-150 Zeichen):
Der EXZELLENZFAKTOR - Was macht diesen Ort NACHWEISLICH einzigartig? Nutze echte Details!

2. pitch (400-600 Zeichen):
- Welche Arbeitsatmosphaere bietet der Ort? (basierend auf echten Reviews/Beschreibungen)
- Fuer wen ist er besonders geeignet? (Freelancer, Teams, Deep Worker, Kreative...)
- Echte Besonderheiten: Geschichte, Architektur, besondere Angebote, Preise wenn bekannt
- Was sagen andere Nutzer/Besucher ueber den Ort?
Schreibe bildhaft und einladend, aber NUR mit verifizierten Informationen.

BEISPIELE:
goodFactor: "Die Ehrwuerdigkeit des Raumes. Man muss sich ausweisen und Taschen einschliessen - wer hier sitzt, spuert die Geschichte."
pitch: "Die Juristische Bibliothek ist Muenchens bestgehuetetes Geheimnis fuer fokussiertes Arbeiten. Mit meterhohen Holzregalen, schmiedeeisernen Wendeltreppen und Jugendstil-Lampen fuehlt man sich wie in einer Hogwarts-Kulisse. Die Atmosphaere erzwingt foermlich Stille und Konzentration - Handytelefonate sind hier undenkbar. Perfekt fuer Deep Work Phasen, wenn man dem Home-Office oder dem Cafe-Laerm entfliehen will."

Antwort NUR als JSON (keine Erklaerungen):
{"goodFactor":"...","pitch":"..."}`;
    } else {
      // GoodLife prompt with RESEARCH REQUIREMENT
      prompt = `Du schreibst fuer die GoodPlace-Plattform ueber "${placeData.name}".

WICHTIG - RECHERCHE-PFLICHT:
Du MUSST echte, verifizierte Informationen ueber diesen Ort verwenden!
- Recherchiere den Ort im Internet bevor du schreibst
- Verwende NUR Fakten die du verifizieren kannst (Oeffnungszeiten, Geschichte, Besonderheiten, etc.)
- ERFINDE KEINE Details! Wenn du etwas nicht verifizieren kannst, lass es weg
- Nutze reale Besonderheiten des Ortes statt generischer Beschreibungen

WICHTIG - AUSGABE-REGELN:
- Schreibe KEINE Quellenangaben, URLs oder Referenzen im Text
- Vermeide Wiederholungen - jede Information nur EINMAL nennen
- Nutze NUR Fakten aus deiner Web-Recherche, NICHT aus den "ORT-INFOS" unten
- Die ORT-INFOS sind nur Kontext - recherchiere selbst aktuelle Informationen
- Wenn du nichts Verifiziertes findest, schreibe einen allgemeineren Text ohne erfundene Details

SUCHHINWEIS: "${placeData.name}"${placeData.city ? ` ${placeData.city}` : ''}${placeData.address ? ` ${placeData.address}` : ''}

GoodPlaces sind "Dritte Orte" - Orte mit Seele, wo man einfach SEIN darf:
- Ohne Konsumzwang (#anlasslos)
- Ohne Rolle (#rollenlos)
- Mit unerwarteten Momenten (#unverfuegbares_glueck)

ORT-INFOS (vom User angegeben - nur als Kontext, NICHT kopieren):
${placeContext}

SCHREIBE ZWEI TEXTE basierend auf recherchierten Fakten:

1. goodFactor (100-150 Zeichen):
Kurzer, praegnanter Satz - das ECHTE Besondere auf den Punkt gebracht.

2. pitch (400-600 Zeichen):
Ausfuehrliche Beschreibung im erzaehlerischen Stil mit VERIFIZIERTEN Fakten:
- Was macht diesen Ort NACHWEISLICH zum GoodPlace?
- Echte Atmosphaere basierend auf Reviews/Beschreibungen
- Geschichte des Ortes, Architektur, besondere Angebote
- Was sagen echte Besucher ueber den Ort?
Schreibe bildhaft und einladend, aber NUR mit verifizierten Informationen.

BEISPIELE:
goodFactor: "Hier wird Kultur demokratisiert. Man darf in den Sesseln luemmeln - ohne Konsumzwang."
pitch: "Die Monacensia ist mehr als eine Bibliothek. In der praechtigen Kuenstlervilla am Friedensengel verschmelzen Literatur und Leben. Zwischen historischen Buchruecken und modernen Lesungen findet jeder seinen Platz - ob im tiefen Ledersessel oder im sonnendurchfluteten Garten. Hier muss man nichts kaufen, nichts leisten. Einfach ankommen, ein Buch aufschlagen und die Stadt vergessen."

Antwort NUR als JSON (keine Erklaerungen):
{"goodFactor":"...","pitch":"..."}`;
    }

    console.log('Generating GoodFactor for:', placeData.name);

    // Configure model with Google Search Grounding for real-time web research
    // Note: Cannot use responseMimeType: 'application/json' with googleSearch tool
    const model = ai.getGenerativeModel({
      model: MODEL,
      generationConfig: {
        temperature: 0.5,  // Lower temperature for more factual, consistent responses
        topP: 0.85,
        maxOutputTokens: 4096,  // Increased for longer responses
        // responseMimeType removed - incompatible with googleSearch tool
      },
      // Enable Google Search for verified information
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tools: [{ googleSearch: {} }] as any,
    });

    // Helper function to check if response is complete
    const isResponseComplete = (text: string): boolean => {
      // Check if JSON is properly closed
      const hasClosingBrace = text.trim().endsWith('}');
      // Check minimum content length (goodFactor ~100 + pitch ~400 = ~500 chars minimum)
      const hasMinLength = text.length > 400;
      return hasClosingBrace && hasMinLength;
    };

    // Retry logic for errors AND truncated responses
    let result;
    let text = '';
    let retries = 3;

    while (retries > 0) {
      try {
        result = await model.generateContent(prompt);

        if (!result) {
          retries--;
          continue;
        }

        const response = result.response;
        text = response.text();

        // Log finish reason to understand truncation
        const candidate = response.candidates?.[0];
        const finishReason = candidate?.finishReason;
        console.log('Gemini finish reason:', finishReason);
        console.log('Gemini raw response length:', text.length, 'chars');

        // Check if response was truncated
        if (finishReason === 'MAX_TOKENS' || finishReason === 'OTHER' || !isResponseComplete(text)) {
          console.warn(`Response appears truncated (finishReason: ${finishReason}, length: ${text.length}). Retrying...`);
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
        }

        // Log grounding metadata if available (shows if web search was used)
        const groundingMetadata = candidate?.groundingMetadata;
        if (groundingMetadata) {
          console.log('Google Search Grounding used - sources:', groundingMetadata.webSearchQueries);
          if (groundingMetadata.groundingChunks) {
            console.log('Grounding chunks:', groundingMetadata.groundingChunks.length, 'sources found');
          }
        } else {
          console.log('No grounding metadata - response generated from model knowledge');
        }

        break; // Success - exit retry loop

      } catch (retryError: unknown) {
        const errorMessage = retryError instanceof Error ? retryError.message : String(retryError);
        if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
          retries--;
          if (retries > 0) {
            console.log(`Gemini overloaded, retrying in 2s... (${retries} left)`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw retryError;
          }
        } else {
          throw retryError;
        }
      }
    }

    if (!result || !text) {
      console.error('Failed to get valid response after retries');
      return null;
    }

    console.log('Gemini raw response:', text);

    // Clean and parse JSON
    let jsonText = text.trim();

    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.slice(7);
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.slice(3);
    }
    if (jsonText.endsWith('```')) {
      jsonText = jsonText.slice(0, -3);
    }
    jsonText = jsonText.trim();

    // Remove any trailing commas before } or ]
    jsonText = jsonText.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');

    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (parseError) {
      console.warn('JSON parse error, trying to extract manually:', parseError);
      console.log('Raw jsonText for debugging:', jsonText);

      // Improved manual extraction using regex that handles escaped quotes
      let goodFactor = '';
      let pitch = '';

      // Extract goodFactor using regex - captures everything between quotes, handling escaped quotes
      const gfMatch = jsonText.match(/"goodFactor"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (gfMatch) {
        goodFactor = gfMatch[1]
          .replace(/\\"/g, '"')  // Unescape quotes
          .replace(/\\n/g, ' ')  // Replace newlines
          .replace(/\s+/g, ' ')  // Normalize whitespace
          .trim();
      }

      // Extract pitch using regex
      const pitchMatch = jsonText.match(/"pitch"\s*:\s*"((?:[^"\\]|\\.)*)"/);
      if (pitchMatch) {
        pitch = pitchMatch[1]
          .replace(/\\"/g, '"')
          .replace(/\\n/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      // If regex failed, try simpler extraction (for truncated responses)
      if (!goodFactor) {
        const gfStart = jsonText.indexOf('"goodFactor"');
        if (gfStart !== -1) {
          const colonPos = jsonText.indexOf(':', gfStart);
          const quoteStart = jsonText.indexOf('"', colonPos) + 1;
          if (quoteStart > 0) {
            // Find closing quote, but skip escaped ones
            let pos = quoteStart;
            while (pos < jsonText.length) {
              const nextQuote = jsonText.indexOf('"', pos);
              if (nextQuote === -1) {
                // No closing quote - take rest of string
                goodFactor = jsonText.substring(quoteStart).replace(/[\n\r]/g, ' ').trim();
                break;
              }
              // Check if quote is escaped
              if (jsonText[nextQuote - 1] !== '\\') {
                goodFactor = jsonText.substring(quoteStart, nextQuote).replace(/[\n\r]/g, ' ').trim();
                break;
              }
              pos = nextQuote + 1;
            }
          }
        }
      }

      if (!pitch) {
        const pitchStart = jsonText.indexOf('"pitch"');
        if (pitchStart !== -1) {
          const colonPos = jsonText.indexOf(':', pitchStart);
          const quoteStart = jsonText.indexOf('"', colonPos) + 1;
          if (quoteStart > 0) {
            // Find closing quote or end of string
            let pos = quoteStart;
            while (pos < jsonText.length) {
              const nextQuote = jsonText.indexOf('"', pos);
              if (nextQuote === -1) {
                // No closing quote - take rest of string (truncated response)
                pitch = jsonText.substring(quoteStart).replace(/[\n\r]/g, ' ').trim();
                // Remove trailing incomplete JSON artifacts
                pitch = pitch.replace(/[}\]]*$/, '').trim();
                break;
              }
              if (jsonText[nextQuote - 1] !== '\\') {
                pitch = jsonText.substring(quoteStart, nextQuote).replace(/[\n\r]/g, ' ').trim();
                break;
              }
              pos = nextQuote + 1;
            }
          }
        }
      }

      // Unescape any remaining escaped characters
      goodFactor = goodFactor.replace(/\\"/g, '"').replace(/\\n/g, ' ');
      pitch = pitch.replace(/\\"/g, '"').replace(/\\n/g, ' ');

      if (goodFactor && pitch) {
        parsed = { goodFactor, pitch };
        console.log('Extracted manually - goodFactor:', goodFactor.length, 'chars');
        console.log('Extracted manually - pitch:', pitch.length, 'chars');
      } else if (goodFactor) {
        // If we only got goodFactor (truncated response), generate a simple pitch
        parsed = {
          goodFactor,
          pitch: `${placeData.name} ist ein besonderer Ort, der zum Verweilen einlaedt. Hier kann man dem Alltag entfliehen und neue Energie tanken.`
        };
        console.log('Partially extracted - goodFactor only:', goodFactor);
      } else {
        throw parseError;
      }
    }

    // Validate minimum lengths - if too short, the response was likely truncated
    const MIN_GOODFACTOR_LENGTH = 50;
    const MIN_PITCH_LENGTH = 150;

    if (parsed.goodFactor && parsed.goodFactor.length < MIN_GOODFACTOR_LENGTH) {
      console.warn(`GoodFactor too short (${parsed.goodFactor.length} chars), may be truncated`);
    }
    if (parsed.pitch && parsed.pitch.length < MIN_PITCH_LENGTH) {
      console.warn(`Pitch too short (${parsed.pitch.length} chars), may be truncated`);
    }

    // Validate response structure
    if (!parsed.goodFactor || !parsed.pitch) {
      console.error('Invalid AI response structure:', parsed);
      return null;
    }

    console.log('Generated GoodFactor:', parsed.goodFactor);
    console.log('Generated Pitch:', parsed.pitch);

    return {
      goodFactor: parsed.goodFactor,
      pitch: parsed.pitch,
      generatedAt: new Date(),
    };
  } catch (error) {
    console.error('Error generating GoodFactor suggestion:', error);
    return null;
  }
}

/**
 * GoodWork data for verification
 */
export interface GoodWorkDataForVerification {
  name: string;
  address?: string;
  city?: string;
  goodworkFactor?: string;
  amenities?: {
    wifi?: string;
    powerOutlets?: string;
    monitors?: boolean;
    printer?: boolean;
    kitchen?: boolean;
    meetingRooms?: boolean;
    phoneBooths?: boolean;
    locker?: boolean;
  };
  pricing?: {
    hourly?: number;
    daily?: number;
    monthly?: number;
    dropIn?: boolean;
  };
  workspaces?: {
    totalSeats?: number;
    quietZone?: boolean;
    teamArea?: boolean;
    standingDesks?: boolean;
    outdoorSeats?: number;
  };
  goodworkEvents?: {
    networking?: boolean;
    workshops?: boolean;
    coworking?: boolean;
    community?: string;
  };
}

/**
 * Verification result for a single field
 */
export interface FieldVerification {
  field: string;
  label: string;
  status: 'verified' | 'incorrect' | 'not_found' | 'updated';
  currentValue?: string;
  foundValue?: string;
  note?: string;
}

/**
 * Complete verification result
 */
export interface GoodWorkVerificationResult {
  summary: string;
  fields: FieldVerification[];
  additionalInfo?: string;
  sources?: string[];
  generatedAt: Date;
}

/**
 * Verify GoodWork place data using AI with Google Search Grounding
 */
export async function verifyGoodWorkData(
  data: GoodWorkDataForVerification
): Promise<GoodWorkVerificationResult | null> {
  if (!ai) {
    console.warn('Gemini AI not initialized - missing API key');
    return null;
  }

  try {
    // Build context from current data
    const dataContext: string[] = [];

    if (data.goodworkFactor) {
      dataContext.push(`GOODWORK-FAKTOR: "${data.goodworkFactor}"`);
    }

    if (data.amenities) {
      const amenitiesInfo: string[] = [];
      if (data.amenities.wifi) amenitiesInfo.push(`WLAN: ${data.amenities.wifi}`);
      if (data.amenities.powerOutlets) amenitiesInfo.push(`Steckdosen: ${data.amenities.powerOutlets}`);
      if (data.amenities.monitors) amenitiesInfo.push('Externe Monitore: Ja');
      if (data.amenities.printer) amenitiesInfo.push('Drucker/Scanner: Ja');
      if (data.amenities.kitchen) amenitiesInfo.push('Kueche/Kaffee: Ja');
      if (data.amenities.meetingRooms) amenitiesInfo.push('Meeting-Raeume: Ja');
      if (data.amenities.phoneBooths) amenitiesInfo.push('Telefonkabinen: Ja');
      if (data.amenities.locker) amenitiesInfo.push('Schliessfaecher: Ja');
      if (amenitiesInfo.length > 0) {
        dataContext.push(`AUSSTATTUNG: ${amenitiesInfo.join(', ')}`);
      }
    }

    if (data.pricing) {
      const pricingInfo: string[] = [];
      if (data.pricing.hourly) pricingInfo.push(`Stundenpreis: ${data.pricing.hourly} EUR`);
      if (data.pricing.daily) pricingInfo.push(`Tagespreis: ${data.pricing.daily} EUR`);
      if (data.pricing.monthly) pricingInfo.push(`Monatspreis: ${data.pricing.monthly} EUR`);
      if (data.pricing.dropIn) pricingInfo.push('Drop-in: Ja');
      if (pricingInfo.length > 0) {
        dataContext.push(`PREISE: ${pricingInfo.join(', ')}`);
      }
    }

    if (data.workspaces) {
      const workspaceInfo: string[] = [];
      if (data.workspaces.totalSeats) workspaceInfo.push(`Gesamtplaetze: ${data.workspaces.totalSeats}`);
      if (data.workspaces.outdoorSeats) workspaceInfo.push(`Outdoor-Plaetze: ${data.workspaces.outdoorSeats}`);
      if (data.workspaces.quietZone) workspaceInfo.push('Ruhebereich: Ja');
      if (data.workspaces.teamArea) workspaceInfo.push('Teambereich: Ja');
      if (data.workspaces.standingDesks) workspaceInfo.push('Steharbeitsplaetze: Ja');
      if (workspaceInfo.length > 0) {
        dataContext.push(`ARBEITSPLAETZE: ${workspaceInfo.join(', ')}`);
      }
    }

    if (data.goodworkEvents) {
      const eventsInfo: string[] = [];
      if (data.goodworkEvents.networking) eventsInfo.push('Networking Events: Ja');
      if (data.goodworkEvents.workshops) eventsInfo.push('Workshops: Ja');
      if (data.goodworkEvents.coworking) eventsInfo.push('Coworking Sessions: Ja');
      if (data.goodworkEvents.community) eventsInfo.push(`Community: "${data.goodworkEvents.community}"`);
      if (eventsInfo.length > 0) {
        dataContext.push(`EVENTS & COMMUNITY: ${eventsInfo.join(', ')}`);
      }
    }

    const prompt = `Du bist ein Fact-Checker fuer die GoodPlace-Plattform.

AUFGABE: Verifiziere die folgenden Angaben ueber "${data.name}" durch Online-Recherche.

SUCHHINWEIS: "${data.name}"${data.city ? ` ${data.city}` : ''}${data.address ? ` ${data.address}` : ''} Coworking Arbeitsplatz Preise Ausstattung Quadratmeter Flaeche

ZU PRUEFENDE DATEN:
${dataContext.length > 0 ? dataContext.join('\n') : 'Keine Daten zum Pruefen angegeben.'}

RECHERCHE-ANWEISUNGEN:
1. Suche nach der offiziellen Website des Ortes
2. Pruefe Preise, Oeffnungszeiten, Ausstattung
3. Suche nach aktuellen Reviews und Bewertungen
4. Vergleiche die angegebenen Daten mit den gefundenen Informationen
5. Sammle ZUSAETZLICHE nuetzliche Informationen (qm, Flaeche, besondere Features, etc.)

ANTWORT-FORMAT (nur JSON, keine Erklaerungen):
{
  "summary": "Kurze Zusammenfassung der Pruefung (1-2 Saetze)",
  "fields": [
    {
      "field": "technischer_feldname",
      "label": "Anzeigename",
      "status": "verified|incorrect|not_found|updated",
      "currentValue": "aktueller Wert (falls vorhanden)",
      "foundValue": "gefundener Wert - NUR der reine Wert, z.B. 50 statt 50 Plaetze",
      "note": "Erklaerung oder Quelle"
    }
  ],
  "additionalInfo": "WICHTIG: Hier alle zusaetzlichen gefundenen Infos sammeln: Quadratmeter, Flaeche, besondere Raeume, Ausstattungsdetails, Kapazitaeten, Events, Besonderheiten die NICHT in den Feldern oben abgedeckt sind. IMMER ausfuellen wenn zusaetzliche Infos gefunden wurden!",
  "sources": ["URL1", "URL2"]
}

STATUS-BEDEUTUNG:
- verified: Angabe stimmt mit Online-Quellen ueberein
- incorrect: Angabe weicht von Online-Quellen ab (foundValue = der korrekte Wert)
- not_found: Konnte online nicht verifiziert werden
- updated: Online wurde aktuellere Information gefunden

WICHTIG ZU foundValue:
- NUR strukturierte Werte angeben die ins Formular passen
- Zahlen: NUR die Zahl, z.B. "50" (nicht "50 Plaetze" oder "ca. 50")
- Preise: NUR die Zahl, z.B. "25" (nicht "25 EUR" oder "ab 25 EUR")
- Boolean: "Ja" oder "Nein"
- WLAN: "none", "basic", "fast" oder "excellent"
- Steckdosen: "few", "some" oder "many"
- Wenn kein strukturierter Wert moeglich: foundValue leer lassen, Info in "note" schreiben

WICHTIG ZU additionalInfo:
- IMMER ausfuellen wenn du zusaetzliche nuetzliche Informationen findest
- Beispiele: Gesamtflaeche in qm, Anzahl Meeting-Raeume, Event-Frequenz, besondere Ausstattung
- Diese Infos erscheinen als Info-Box und werden vom Admin manuell eingetragen

WICHTIG:
- Pruefe ALLE angegebenen Felder
- Sei gruendlich bei der Recherche
- Nenne konkrete Quellen wenn moeglich
- Bei Preisen: Pruefe ob aktuell (${new Date().getFullYear()})
- Bei Hotels/Restaurants: Oft kein klassisches Coworking - trotzdem Arbeitsmoeglichkeiten pruefen`;

    console.log('Verifying GoodWork data for:', data.name);

    // Configure model with Google Search Grounding
    const model = ai.getGenerativeModel({
      model: MODEL,
      generationConfig: {
        temperature: 0.3,  // Very low for factual verification
        topP: 0.8,
        maxOutputTokens: 8192,  // Higher limit for detailed verification
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tools: [{ googleSearch: {} }] as any,
    });

    // Retry logic
    let result;
    let text = '';
    let retries = 3;

    while (retries > 0) {
      try {
        result = await model.generateContent(prompt);

        if (!result) {
          retries--;
          continue;
        }

        const response = result.response;
        text = response.text();

        const candidate = response.candidates?.[0];
        const finishReason = candidate?.finishReason;
        console.log('Verification finish reason:', finishReason);
        console.log('Verification response length:', text.length, 'chars');

        // Log grounding metadata
        const groundingMetadata = candidate?.groundingMetadata;
        if (groundingMetadata) {
          console.log('Verification used Google Search:', groundingMetadata.webSearchQueries);
        }

        // Check for truncation - but if response is long enough, we can extract partial data
        if (finishReason === 'MAX_TOKENS' || !text.trim().endsWith('}')) {
          // If we have a substantial response (>5000 chars), don't retry - we can extract partial data
          if (text.length > 5000) {
            console.log('Response truncated but long enough to extract partial data, proceeding...');
            break;
          }
          console.warn('Verification response truncated and too short, retrying...');
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
        }

        break;
      } catch (retryError: unknown) {
        const errorMessage = retryError instanceof Error ? retryError.message : String(retryError);
        if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
          retries--;
          if (retries > 0) {
            console.log(`Gemini overloaded, retrying in 2s... (${retries} left)`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw retryError;
          }
        } else {
          throw retryError;
        }
      }
    }

    if (!result || !text) {
      console.error('Failed to get verification response');
      return null;
    }

    console.log('Verification raw response:', text.length, 'chars');

    // Parse JSON
    let jsonText = text.trim();
    if (jsonText.startsWith('```json')) jsonText = jsonText.slice(7);
    if (jsonText.startsWith('```')) jsonText = jsonText.slice(3);
    if (jsonText.endsWith('```')) jsonText = jsonText.slice(0, -3);
    jsonText = jsonText.trim();

    // Sanitize JSON - remove control characters that break parsing
    // eslint-disable-next-line no-control-regex
    jsonText = jsonText.replace(/[\x00-\x1F\x7F]/g, (char) => {
      // Keep newlines and tabs as they may be intended
      if (char === '\n' || char === '\r' || char === '\t') return char;
      return ' ';
    });

    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (parseError) {
      console.warn('JSON parse failed, trying to extract partial data:', parseError);
      console.log('Raw JSON length:', jsonText.length);

      // Try to extract what we can from truncated JSON
      try {
        // Extract summary
        const summaryMatch = jsonText.match(/"summary"\s*:\s*"((?:[^"\\]|\\.)*)"/);
        const summary = summaryMatch ? summaryMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ') : null;

        // Extract fields array - find all complete field objects
        const fields: FieldVerification[] = [];
        const fieldRegex = /\{\s*"field"\s*:\s*"([^"]+)"\s*,\s*"label"\s*:\s*"([^"]+)"\s*,\s*"status"\s*:\s*"(verified|incorrect|not_found|updated)"[^}]*\}/g;
        let fieldMatch;
        while ((fieldMatch = fieldRegex.exec(jsonText)) !== null) {
          const fullMatch = fieldMatch[0];
          try {
            // Try to parse the individual field object
            const fieldObj = JSON.parse(fullMatch);
            fields.push(fieldObj);
          } catch {
            // If parsing fails, extract manually
            const currentValueMatch = fullMatch.match(/"currentValue"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const foundValueMatch = fullMatch.match(/"foundValue"\s*:\s*"((?:[^"\\]|\\.)*)"/);
            const noteMatch = fullMatch.match(/"note"\s*:\s*"((?:[^"\\]|\\.)*)"/);

            fields.push({
              field: fieldMatch[1],
              label: fieldMatch[2],
              status: fieldMatch[3] as 'verified' | 'incorrect' | 'not_found' | 'updated',
              currentValue: currentValueMatch ? currentValueMatch[1].replace(/\\"/g, '"') : undefined,
              foundValue: foundValueMatch ? foundValueMatch[1].replace(/\\"/g, '"') : undefined,
              note: noteMatch ? noteMatch[1].replace(/\\"/g, '"') : undefined,
            });
          }
        }

        // Extract additionalInfo
        const additionalMatch = jsonText.match(/"additionalInfo"\s*:\s*"((?:[^"\\]|\\.)*)"/);
        const additionalInfo = additionalMatch ? additionalMatch[1].replace(/\\"/g, '"').replace(/\\n/g, ' ') : undefined;

        if (summary || fields.length > 0) {
          console.log('Extracted partial verification:', { summary: !!summary, fieldsCount: fields.length });
          parsed = {
            summary: summary || 'Verifikation teilweise abgeschlossen (Antwort war unvollständig).',
            fields,
            additionalInfo,
          };
        } else {
          console.error('Could not extract any verification data');
          return null;
        }
      } catch (extractError) {
        console.error('Failed to extract partial verification data:', extractError);
        return null;
      }
    }

    return {
      summary: parsed.summary || 'Verifikation abgeschlossen.',
      fields: parsed.fields || [],
      additionalInfo: parsed.additionalInfo,
      sources: parsed.sources,
      generatedAt: new Date(),
    };
  } catch (error) {
    console.error('Error verifying GoodWork data:', error);
    return null;
  }
}

// ============================================
// PLACE IMPORT (KI-basierter Import aus Freitext)
// ============================================

/**
 * Result structure for AI-based place import
 */
export interface PlaceImportResult {
  // Basic
  name: string;
  tagline: string;
  description?: string;
  goodFactor?: string;

  // Realm Detection
  suggestedRealm: 'goodlife' | 'goodwork' | 'both';

  // Categories
  category?: string;           // MainCategory for GoodLife
  goodworkCategory?: string;   // GoodWorkCategory for GoodWork

  // Location
  address?: string;
  city?: string;
  postalCode?: string;
  district?: string;

  // GoodLife Fields
  moods?: string[];
  activities?: string[];
  vibeTags?: string[];

  // GoodWork Fields
  goodworkVibeTags?: string[];
  goodworkFactor?: string;
  amenitiesText?: string;  // Free text for equipment description
  amenities?: {
    monitors?: boolean;
    printer?: boolean;
    kitchen?: boolean;
    meetingRooms?: boolean;
    phoneBooths?: boolean;
    locker?: boolean;
  };
  pricing?: {
    hourly?: number;
    daily?: number;
    monthly?: number;
    dropIn?: boolean;
    priceNote?: string;
  };

  // Contact
  website?: string;
  phone?: string;
  email?: string;

  // Hours
  openingHoursText?: string;
}

/**
 * Process free text input and extract place information using AI
 * Uses Google Search Grounding to find and verify real information
 */
export async function processPlaceImport(text: string): Promise<PlaceImportResult | null> {
  if (!ai) {
    console.warn('Gemini AI not initialized - missing API key');
    return null;
  }

  try {
    // Extract potential name from text for search
    const firstLine = text.split('\n')[0].trim();
    const potentialName = firstLine.length < 100 ? firstLine : firstLine.substring(0, 100);

    const prompt = `Du bist ein Experte fuer die GoodPlace-Plattform und analysierst Texte ueber Orte.

AUFGABE: Analysiere den folgenden Text und extrahiere alle relevanten Informationen ueber den Ort.
NUTZE DIE WEB-SUCHE um fehlende Informationen (besonders Adresse, Oeffnungszeiten, Kontakt) zu finden!

SUCHHINWEIS: "${potentialName}" - Suche nach diesem Ort um vollstaendige Daten zu finden.

WICHTIG: Antworte NUR mit validem JSON. Keine Erklaerungen, kein Markdown.

REALM-ERKENNUNG - KRITISCH:
- "goodlife": Cafe, Restaurant, Bar, Park, Bibliothek, oeffentlicher Raum, Kulturort (Freizeit & Begegnung)
- "goodwork": Coworking Space, Bueroflaeche, Meeting-Raum, Arbeitsort, Maker Space (Arbeit & Kreativitaet)
- "both": Orte die beides sind (z.B. Cafe mit gutem Arbeitsbereich, Bibliothek zum Arbeiten)

KATEGORIEN GOODLIFE (category) - NUR fuer suggestedRealm "goodlife" oder "both":
- "urban_creative": Urbane Oasen & Kreatives (Ateliers, Kulturzentren)
- "public_living_room": Oeffentliche Wohnzimmer (Cafes, Bars mit Wohnzimmer-Charakter)
- "silent_sanctuary": Stille & Rueckzug (Bibliotheken, ruhige Orte)
- "analoge_refugien": Analoge Refugien (Traditionelle Orte mit Geschichte)
- "natur_outdoor": Natur & Outdoor (Parks, Gaerten, Aussenraeume)

KATEGORIEN GOODWORK (goodworkCategory) - NUR fuer suggestedRealm "goodwork" oder "both":
- "coworking": Coworking Space (gemeinsam arbeiten)
- "focused_work": Fokusraum (konzentriertes Arbeiten)
- "creative_studio": Kreativstudio (fuer kreative Berufe)
- "meeting_space": Meeting & Events
- "maker_space": Maker & Werkstatt
- "learning_hub": Lern- & Bildungsort

KRITISCH - KATEGORIE-ZUWEISUNG:
- Bei suggestedRealm = "goodwork": NUR goodworkCategory setzen, KEIN category!
- Bei suggestedRealm = "goodlife": NUR category setzen, KEIN goodworkCategory!
- Bei suggestedRealm = "both": BEIDE setzen (category UND goodworkCategory)

VIBE-TAGS GOODLIFE (vibeTags) - nur fuer GoodLife/both:
"rollenlos", "schnack_potential", "unverfuegbares_glueck", "offen_fuer_alle", "selbstwirksam", "antiquiert_gut", "empfangsbereit", "anlasslos", "selbstueberraschung"

VIBE-TAGS GOODWORK (goodworkVibeTags) - nur fuer GoodWork/both:
"fokussiert", "kollaborativ", "inspirierend", "flexibel", "professionell", "community", "gruendertauglich", "nachhaltig", "outdoor", "naturverbunden"

MOODS (moods - nur fuer GoodLife/both):
"Ruhig & entspannt", "Lebendig & gesellig", "Kreativ & inspirierend", "Gemuetlich & vertraut", "Offen & einladend", "Traditionell & authentisch"

ACTIVITIES (activities - nur fuer GoodLife/both):
"Einfach da sein", "Lesen & Schmoekern", "Plaudern & Zuhoeren", "Spielen (Karten, Brettspiele)", "Neue Menschen kennenlernen", "Stammtisch besuchen", "Events & Veranstaltungen", "Kreativ sein"

JSON-STRUKTUR:
{
  "name": "Name des Ortes",
  "tagline": "Kurzbeschreibung (max 100 Zeichen)",
  "description": "Ausfuehrliche Beschreibung des Ortes",
  "goodFactor": "Was macht diesen Ort besonders? (fuer GoodLife) - 100-200 Zeichen",
  "goodworkFactor": "Warum ist das ein guter Arbeitsort? (fuer GoodWork) - 100-200 Zeichen",

  "suggestedRealm": "goodlife" | "goodwork" | "both",

  "category": "Kategorie fuer GoodLife (NUR wenn goodlife oder both)",
  "goodworkCategory": "Kategorie fuer GoodWork (NUR wenn goodwork oder both)",

  "address": "Strasse Hausnummer - MUSS via Web-Suche gefunden werden!",
  "city": "Stadt",
  "postalCode": "PLZ",
  "district": "Stadtteil/Bezirk",

  "moods": ["mood1", "mood2"],
  "activities": ["activity1", "activity2"],
  "vibeTags": ["tag1", "tag2"],

  "goodworkVibeTags": ["tag1", "tag2"],
  "amenitiesText": "Freitext-Beschreibung der Ausstattung: WLAN-Qualitaet, Steckdosen, Arbeitsplaetze, etc.",
  "amenities": {
    "monitors": false,
    "printer": false,
    "kitchen": true,
    "meetingRooms": false,
    "phoneBooths": false,
    "locker": false
  },
  "pricing": {
    "hourly": 5,
    "daily": 25,
    "monthly": 300,
    "dropIn": true,
    "priceNote": "Zusaetzliche Preis-Infos"
  },

  "website": "https://...",
  "phone": "0123 456789",
  "email": "info@...",

  "openingHoursText": "Mo-Fr 8-18 Uhr, Sa 10-14 Uhr"
}

REGELN:
1. NUTZE WEB-SUCHE fuer Adresse, Website, Telefon, Oeffnungszeiten!
2. Generiere description, goodFactor/goodworkFactor basierend auf deiner Recherche
3. Waehle passende Kategorien basierend auf dem Ort-Typ
4. Waehle passende Tags und Moods basierend auf der Atmosphaere
5. Bei suggestedRealm="goodwork": KEIN category-Feld, nur goodworkCategory!
6. Bei suggestedRealm="goodlife": KEIN goodworkCategory-Feld, nur category!
7. amenitiesText: Beschreibe WLAN, Steckdosen, Plaetze etc. als Freitext
8. Alle Texte auf Deutsch

TEXT ZUM ANALYSIEREN:
${text}`;

    console.log('Processing place import with web search, text length:', text.length);

    // Use model with Google Search Grounding for real data lookup
    const model = ai.getGenerativeModel({
      model: MODEL,
      generationConfig: {
        temperature: 0.3,  // Low temperature for accurate extraction
        topP: 0.8,
        maxOutputTokens: 8192,
      },
      // Enable Google Search for finding real place data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tools: [{ googleSearch: {} }] as any,
    });

    // Retry logic
    let result;
    let retries = 3;
    while (retries > 0) {
      try {
        result = await model.generateContent(prompt);
        break;
      } catch (retryError: unknown) {
        const errorMessage = retryError instanceof Error ? retryError.message : String(retryError);
        if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
          retries--;
          if (retries > 0) {
            console.log(`Gemini overloaded, retrying in 2s... (${retries} left)`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw retryError;
          }
        } else {
          throw retryError;
        }
      }
    }

    if (!result) {
      return null;
    }

    const response = result.response;
    const responseText = response.text();

    // Log grounding info
    const candidate = response.candidates?.[0];
    const groundingMetadata = candidate?.groundingMetadata;
    if (groundingMetadata) {
      console.log('Place import used Google Search:', groundingMetadata.webSearchQueries);
    }

    console.log('Place import raw response:', responseText.length, 'chars');
    console.log('Place import response:', responseText);

    // Parse JSON
    let jsonText = responseText.trim();
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.slice(7);
    }
    if (jsonText.startsWith('```')) {
      jsonText = jsonText.slice(3);
    }
    if (jsonText.endsWith('```')) {
      jsonText = jsonText.slice(0, -3);
    }
    jsonText = jsonText.trim();

    const parsed = JSON.parse(jsonText);

    // Validate required fields
    if (!parsed.name) {
      console.error('Place import: Missing required field "name"');
      return null;
    }

    // Set defaults
    if (!parsed.tagline) {
      parsed.tagline = parsed.name;
    }
    if (!parsed.suggestedRealm) {
      parsed.suggestedRealm = 'goodlife';
    }

    // Enforce category rules based on realm
    if (parsed.suggestedRealm === 'goodwork') {
      // GoodWork only - remove GoodLife category
      delete parsed.category;
      parsed.moods = undefined;
      parsed.activities = undefined;
      parsed.vibeTags = undefined;
      parsed.goodFactor = undefined;
    } else if (parsed.suggestedRealm === 'goodlife') {
      // GoodLife only - remove GoodWork fields
      delete parsed.goodworkCategory;
      parsed.goodworkVibeTags = undefined;
      parsed.goodworkFactor = undefined;
      parsed.amenitiesText = undefined;
      parsed.amenities = undefined;
      parsed.pricing = undefined;
    }
    // For 'both', keep all fields

    console.log('Place import successful:', parsed.name, 'realm:', parsed.suggestedRealm);

    return parsed as PlaceImportResult;
  } catch (error) {
    console.error('Error processing place import:', error);
    return null;
  }
}

/**
 * KI-Textbereinigung: Formatiert einen unstrukturierten Text in einen konsistenten, lesbaren Text
 * @param text Der zu bereinigende Text
 * @param textType Art des Texts ('amenities' oder 'additionalInfo')
 * @param placeName Name des Places für Kontext
 * @returns Bereinigter Text oder null bei Fehler
 */
export async function reformatText(
  text: string,
  textType: 'amenities' | 'additionalInfo',
  placeName: string
): Promise<string | null> {
  if (!ai) {
    console.error('Gemini AI not available');
    return null;
  }

  if (!text || text.trim().length === 0) {
    return null;
  }

  try {
    const model = ai.getGenerativeModel({ model: MODEL });

    let prompt: string;

    if (textType === 'amenities') {
      prompt = `Du bist ein Texteditor für eine Coworking-Space / GoodWork-Place Datenbank.

Aufgabe: Formatiere die folgenden unstrukturierten Informationen über Ausstattung und Möglichkeiten zu einem gut lesbaren, konsistenten Text.

Place: ${placeName}

Eingabe-Text:
${text}

Regeln:
1. Fasse zusammengehörige Informationen logisch zusammen
2. Verwende kurze, prägnante Sätze
3. Entferne Duplikate und widersprüchliche Informationen
4. Verwende eine einheitliche Sprache (Deutsch)
5. Strukturiere nach Kategorien wenn sinnvoll (z.B. Internet, Arbeitsplätze, Ausstattung)
6. Halte den Text kompakt - maximal 3-4 kurze Absätze
7. Keine Aufzählungszeichen, sondern Fließtext oder kommagetrennte Listen

Gib NUR den bereinigten Text zurück, keine Erklärungen oder Überschriften.`;
    } else {
      prompt = `Du bist ein Texteditor für eine Coworking-Space / GoodWork-Place Datenbank.

Aufgabe: Formatiere die folgenden unstrukturierten Zusatzinformationen zu einem gut lesbaren, konsistenten Text.

Place: ${placeName}

Eingabe-Text:
${text}

Regeln:
1. Fasse zusammengehörige Informationen logisch zusammen
2. Verwende kurze, prägnante Sätze
3. Entferne Duplikate und widersprüchliche Informationen
4. Verwende eine einheitliche Sprache (Deutsch)
5. Sortiere nach Relevanz (Preise, Öffnungszeiten, Besonderheiten)
6. Halte den Text kompakt - maximal 3-4 kurze Absätze
7. Keine Aufzählungszeichen, sondern Fließtext oder kommagetrennte Listen

Gib NUR den bereinigten Text zurück, keine Erklärungen oder Überschriften.`;
    }

    const result = await model.generateContent(prompt);
    const response = result.response;
    const reformattedText = response.text().trim();

    if (!reformattedText) {
      return null;
    }

    return reformattedText;
  } catch (error) {
    console.error('Error reformatting text:', error);
    return null;
  }
}
